/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import {get, getApplicationContext} from '@ion/core/src/environment/environment';
import {translate} from '@ion/core/src/locale/locale';
import {error} from '@ion/core/src/logger/logger';
import UserPreferences from '@ion/core/src/preferences/UserPreferences';
import {
  deleteUser as destroyUser,
  deleteUsers as destroyUsers,
  getAccountRoles,
  getAccountRolesNested,
  getUserExtendedAttributes,
  loadUsers,
  rolesSearch,
  saveUserExtendedAttributes
} from '@ion/core/src/services/Connect';
import {entityId} from '@ion/core/src/services/ConnectUtils';
import {
  createUser,
  getUserContext,
  updateUser,
  updateUsers,
  UserContext
} from '@ion/core/src/services/MicroServices';
import {
  EnhancedUser,
  EnhancedUsers,
  Entity,
  ExtendedAttribute,
  GenericObject,
  User,
  Users
} from '@ion/core/src/types/entities';
import {Preference} from '@ion/core/src/types/preferences';
import {LoadUsersParams} from '@ion/core/src/types/services';
import {setUserUnit} from '@ion/core/src/units/helpers';
import {deepCopy} from '@ion/core/src/utils/ObjectUtil';
import {getLanguageExtendedAttribute} from '@ion/dls/src/editors/UserEditorUtils';
import {FilterModel} from '@ion/dls/src/grid/IonGridUtils';
import {ApplicationKeys} from '@/types/application';
import {
  AsyncActionCreator,
  PageInitializeAction
} from '@/types/redux';
import {
  errorAddOptions,
  errorEditOptions,
  successAddOptions,
  successEditOptions
} from '../../components/prompts/constants';
import {UPDATE_CURRENT_USER} from '../actions/login';
import {getAccountId} from '../helpers';
import {importConfirm} from './importer';
import {toggleUserPrompt} from './user-prompt';

// name of the reducer
export const NAME                       = 'adminUsers';

// initialize users ...
export const PRE_INITIALIZE_ADMIN_USERS = 'PRE_INITIALIZE_ADMIN_USERS';
export const INITIALIZE_ADMIN_USERS     = 'INITIALIZE_ADMIN_USERS';
export const RE_INITIALIZE_ADMIN_USERS  = 'RE_INITIALIZE_ADMIN_USERS';

// grid column state
export const SET_GRID_COLUMNS           = 'SET_ADMIN_USERS_GRID_COLUMNS';
export const UPDATE_GRID_COLUMNS        = 'UPDATE_ADMIN_USERS_GRID_COLUMNS';

export const ADMIN_USERS_GRID_REFRESHED = 'ADMIN_USERS_GRID_REFRESHED';
export const ADMIN_USERS_LOAD_ROLES     = 'ADMIN_USERS_LOAD_ROLES';

// users retrieval ...
export const GET_ADMIN_USERS_REQUEST    = 'GET_ADMIN_USERS_REQUEST';
export const GET_ADMIN_USERS_RESPONSE   = 'GET_ADMIN_USERS_RESPONSE';
export const GET_ADMIN_USERS_ERROR      = 'GET_ADMIN_USERS_ERROR';

// user bulk import ...
export const IMPORT_ADMIN_USERS         = 'IMPORT_ADMIN_USERS';

// user add ...
export const ADD_ADMIN_USER             = 'ADD_ADMIN_USER';
export const ADD_ADMIN_USER_REQUEST     = 'ADD_ADMIN_USER_REQUEST';
export const ADD_ADMIN_USER_RESPONSE    = 'ADD_ADMIN_USER_RESPONSE';
export const ADD_ADMIN_USER_ERROR       = 'ADD_ADMIN_USER_ERROR';

// user edit ...
export const EDIT_ADMIN_USER            = 'EDIT_ADMIN_USER';
export const EDIT_ADMIN_USER_REQUEST    = 'EDIT_ADMIN_USER_REQUEST';
export const EDIT_ADMIN_USER_RESPONSE   = 'EDIT_ADMIN_USER_RESPONSE';
export const EDIT_ADMIN_USER_ERROR      = 'EDIT_ADMIN_USER_ERROR';

// user delete ...
export const DELETE_ADMIN_USER          = 'DELETE_ADMIN_USER';
export const DELETE_ADMIN_USER_REQUEST  = 'DELETE_ADMIN_USER_REQUEST';
export const DELETE_ADMIN_USER_RESPONSE = 'DELETE_ADMIN_USER_RESPONSE';
export const DELETE_ADMIN_USER_ERROR    = 'DELETE_ADMIN_USER_ERROR';

// view user audit log ...
export const AUDIT_ADMIN_USER           = 'AUDIT_ADMIN_USER';
export const AUDIT_ADMIN_USER_REQUEST   = 'AUDIT_ADMIN_USER_REQUEST';
export const AUDIT_ADMIN_USER_RESPONSE  = 'AUDIT_ADMIN_USER_RESPONSE';
export const AUDIT_ADMIN_USER_ERROR     = 'AUDIT_ADMIN_USER_ERROR';

// bulk user edits ...
export const EDIT_ADMIN_USERS            = 'EDIT_ADMIN_USERS';
export const EDIT_ADMIN_USERS_REQUEST    = 'EDIT_ADMIN_USERS_REQUEST';
export const EDIT_ADMIN_USERS_RESPONSE   = 'EDIT_ADMIN_USERS_RESPONSE';
export const EDIT_ADMIN_USERS_ERROR      = 'EDIT_ADMIN_USERS_ERROR';

// bulk user deletes ...
export const DELETE_ADMIN_USERS          = 'DELETE_ADMIN_USERS';
export const DELETE_ADMIN_USERS_REQUEST  = 'DELETE_ADMIN_USERS_REQUEST';
export const DELETE_ADMIN_USERS_RESPONSE = 'DELETE_ADMIN_USERS_RESPONSE';
export const DELETE_ADMIN_USERS_ERROR    = 'DELETE_ADMIN_USERS_ERROR';

// admin users filter model ...
export const SET_ADMIN_USERS_GRID_FILTER_MODEL = 'SET_ADMIN_USERS_GRID_FILTER_MODEL';

/**
 * @initialize
 *
 * In order to ensure the user profile editor has been initialized, all iOn application
 * roles must be retrieved prior to enabling of the user editor (i.e. user profile).
 */
export const preinitializeAdminUsers = (accountId: number) => {
  return async(dispatch: Function, getState: Function): Promise<void> => {
    try {
      const roles = await getAccountRolesNested(accountId);
      dispatch({type: PRE_INITIALIZE_ADMIN_USERS, roles});
    } catch (err) {
      dispatch({type: PRE_INITIALIZE_ADMIN_USERS});
      error(err);
    }
  };
};

/**
 * @initialize
 */
export const initializeAdminUsers = () => ({
  type : INITIALIZE_ADMIN_USERS
});

export const reInitializeAdminUsers = () => ({
  type : RE_INITIALIZE_ADMIN_USERS
});

export const adminUsersLoadRoles = () => ({
  type : ADMIN_USERS_LOAD_ROLES
});

/**
 * @adminUsersGridRefreshed
 */
export const adminUsersGridRefreshed = (): PageInitializeAction => {
  return {type: ADMIN_USERS_GRID_REFRESHED};
};

/**
 * @getAdminUsers
 */
export const getAdminUsers = ({
  pageId = 1,
  pageSize = 20,
  sort,
  filter,
  includeSubGroups = false,
  callback: callbackFn
}) => {
  return async(dispatch: Function, getState: Function): Promise<any> => {
    dispatch({type: GET_ADMIN_USERS_REQUEST});
    const accountId = getAccountId(getState);
    let {loadRolesPending, roles} = getState().adminUsers;
    const data: LoadUsersParams = {
      accountId,
      pageId,
      pageSize,
      includeSubGroups
    };
    if (sort) {
      data.sortOn = sort;
    }
    if (filter) {
      data.filterOn = filter;
    }
    try {
      const {users: items, totalCount} = await loadUsers(data);
      if (loadRolesPending) {
        roles = await getAccountRoles(accountId, includeSubGroups);
        dispatch({type: PRE_INITIALIZE_ADMIN_USERS, roles});
      }
      dispatch({type: GET_ADMIN_USERS_RESPONSE});
      callbackFn?.({items, totalCount}, roles);
      return {items, totalCount};
    } catch (err) {
      error(err);
      callbackFn({error: err, items: [], totalCount: 0});
      dispatch({type: GET_ADMIN_USERS_ERROR, error: err});
      return err;
    }
  };
};

/**
 * Invoked when the Bulk Import Admin User Dialog is to be displayed or removed.
 */
export const importAdminUsers = (show = true, result = null) => {
  return async(dispatch: Function) => {
    dispatch(importConfirm(Entity.user));
    dispatch({type: IMPORT_ADMIN_USERS, show, result});
  };
};

/**
 * @addAdminUser
 *
 * Invoked when the Add Admin User editor is to be displayed or removed.
 */
export const addAdminUser = (show = true) => ({
  type : ADD_ADMIN_USER, show
});

/**
 * @addUser
 *
 * Invoked when a new user is to be created; consolidating the process of User
 * creation into a single transaction. User creation is proxied through the
 * MicroServices which manage sending out the new user confirmation emails,
 * etc.
 *
 */
export const addUser = (user: EnhancedUser): AsyncActionCreator => {
  return async(dispatch: Function): Promise<void> => {
    dispatch({type: ADD_ADMIN_USER_REQUEST, user});
    delete user.settings;
    try {
      const created = await createUser(user, getUserContext(getApplicationContext()));
      dispatch({type: ADD_ADMIN_USER_RESPONSE, user: created});
      dispatch(toggleUserPrompt(successAddOptions(Entity.user)));
    } catch (err) {
      dispatch({type: ADD_ADMIN_USER_ERROR, error: err});
      dispatch(toggleUserPrompt(errorAddOptions(Entity.user, err)));
      error(err);
    }
  };
};

/**
 * @editAdminUser
 *
 * Invoked when the Edit Admin User editor is to be displayed or removed. If the user
 * being edited is the current user (i.e. the logged in user, pass the isCurrentUser
 * argument as true).
 */
export const editAdminUser = (user: User, show = true) => {
  return async(dispatch: Function, getState: Function): Promise<void> => {
    const isCurrentUser = getState().login?.user.id === user.id;
    dispatch({type: EDIT_ADMIN_USER, user, show, isCurrentUser});
  };
};

/**
 * @removeMultipleRoles
 *
 * Private func, invoked from editUser that finds all iOn roles currently assigned
 * to user. Keeps only the selected role from UI and removes the other iOn roles.
 */
const removeMultipleRoles = async(user: EnhancedUser): Promise<EnhancedUser> => {
  const mutableUserObj = deepCopy(user) || {};
  const {roles}        = mutableUserObj;
  const assignedRole   = roles?.pop();
  if (roles.length) {
    const userRolesMap = {};
    roles.forEach(role => {
      userRolesMap[entityId(role.href)] = role;
    });
    const iOnRoles = await rolesSearch({
      id : {
        value    : Object.keys(userRolesMap).toString(),
        wildcard : false
      },
      application : {
        value    : get('appKey'),
        wildcard : false
      }
    });
    iOnRoles.forEach(({id}) => {
      if (userRolesMap[id]) {
        delete userRolesMap[id];
      }
    });
    mutableUserObj.roles = Object.values(userRolesMap);
  }
  mutableUserObj.roles?.push(assignedRole);
  return mutableUserObj;
};

/**
 * @editUser
 *
 * Invoked when an existing user is to be updated; consolidating the process of
 * User updates into a single transaction. The process for editing a User is
 * outlined below.
 *
 * 1.) Update the User against CTC Services (proxying through the MicroServices
 *     so as to facilitate the new email confirmation email).
 * 2). Update User ExtendedAttributes and / or Preferences:
 *     2.1) If the user is not the current user, update their extendedAttributes.
 *          For more information, see the updateUserExtendedAttributes function.
 *
 */
export const editUser = (user: EnhancedUser, isCurrentUser?: boolean): Function => {
  return async(dispatch: Function, getState: Function): Promise<any> => {
    dispatch({type: EDIT_ADMIN_USER_REQUEST});
    try {
      if (!isCurrentUser) {
        user = await removeMultipleRoles(user);
      }
      const updated = await updateUser(user, getUserContext(getApplicationContext()));
      if (!isCurrentUser) {
        if (user.extendedAttribute) {
          updateUserExtendedAttributes(updated.id, user.extendedAttribute);
        }
      } else {
        dispatch({type: UPDATE_CURRENT_USER, updated});
        const {account} = getState().login;
        // the Language field saved above lives in the User's own extended
        // attribute, not on account.language (a group-level fallback only) -
        // without this, the local preference never picks up the user's own
        // choice, so LanguageChangePrompt never sees a change to warn about.
        // The namespace must match what UserEditor saved it under (the app
        // key string, not the environment's `appKey` UUID) ...
        const preferredLanguage = getLanguageExtendedAttribute(ApplicationKeys.iOnSuite, updated);
        if (preferredLanguage) {
          UserPreferences.setLanguage(preferredLanguage);
        }
        UserPreferences.persistDefaultLanguage(account?.language);
        const timeZoneOption = UserPreferences.get(Preference.userTimeZone)?.timeZoneOption || 'User';
        const userTimeZone   = {timeZone: user.timeZone, timeZoneOption};
        UserPreferences.set(Preference.userTimeZone, userTimeZone);
        UserPreferences.save();
        setUserUnit(user?.systemOfUnits.value as any);
      }
      dispatch({type: EDIT_ADMIN_USER_RESPONSE, user: updated, isCurrentUser});
      dispatch(toggleUserPrompt(successEditOptions(Entity.user)));
    } catch (err) {
      dispatch({type: EDIT_ADMIN_USER_ERROR, error: err});
      dispatch(toggleUserPrompt(errorEditOptions(Entity.user, err)));
      error(err);
    }
  };
};

/**
 * @editAdminUsers
 *
 * Invoked when the Bulk Edit Admin Users Dialog is to be displayed or removed.
 */
export const editAdminUsers = (users: EnhancedUsers, show = true) => {
  return async(dispatch: Function): Promise<void> => {
    dispatch({type: EDIT_ADMIN_USERS, users, show});
  };
};

/**
 * @editUsers
 *
 * Invoked when a collection of existing users are to be updated; consolidating the process of
 * Users updates into a single transaction. Update the List of Users against CTC Services
 * (proxying through the MicroServices so as to facilitate the new email confirmation email).
 */
export const editUsers = (users: EnhancedUsers): Function => {
  return async(dispatch: Function): Promise<any> => {
    dispatch({type: EDIT_ADMIN_USERS_REQUEST});
    try {
      const {results, errors} = await updateUsers(users, UserContext.suite);
      dispatch(toggleUserPrompt(successEditOptions(translate('common:entityLabels.users'),
        {results, items: users})));
      dispatch({type: EDIT_ADMIN_USERS_RESPONSE, users: results});
    } catch (err) {
      dispatch({type: EDIT_ADMIN_USERS_ERROR, error: err});
      dispatch(toggleUserPrompt(errorEditOptions(translate('common:entityLabels.users'))));
      error(err);
    }
  };
};

/**
 * @auditAdminUser
 *
 * Invoked when the the user auditor is to be displayed or removed.
 */
export const auditAdminUser = (user: User, show = true) => ({
  type : AUDIT_ADMIN_USER, user, show
});

/**
 * @deleteAdminUser
 *
 * Invoked when the Delete Admin User Dialog is to be displayed or removed.
 */
export const deleteAdminUser = (user: User, show = true) => ({
  type : DELETE_ADMIN_USER, user, show
});

/**
 * @deleteUser
 *
 * Invoked when an existing user is to be deleted, delegating to the deleteUser
 * CTC API and dispatching a successful response or error action accordingly.
 */
export const deleteUser = (user: EnhancedUser) => {
  return async(dispatch: Function): Promise<any> => {
    dispatch({type: DELETE_ADMIN_USER_REQUEST});
    try {
      const deleted = await destroyUser(user);
      dispatch({type: DELETE_ADMIN_USER_RESPONSE, user: deleted});
    } catch (err) {
      dispatch({type: DELETE_ADMIN_USER_ERROR, error: err});
      error(err);
    }
  };
};

/**
 * @updateUserExtendedAttributes
 *
 * Provides a utility which manages setting or resetting the user's extendedAttributes.
 *
 * @param   {number} id The id of the user for which extendedAttributes are to be
 *          updated or created.
 * @param   {Promise<ExtendedAttribute>} A Promise which always resolves the current
 *          ExtendedAttribute for the user.
 */
const updateUserExtendedAttributes = async(id: number, {value}: ExtendedAttribute = {value: null}) => {
  try {
    const current = await getUserExtendedAttributes(id);
    if (current.value) {
      if (value) {
        const {fleetOutlook} = value as GenericObject;
        current.value.fleetOutlook = fleetOutlook;
      } else {
        delete current.value?.fleetOutlook;
      }
      saveUserExtendedAttributes(id, current.value);
    } else {
      saveUserExtendedAttributes(id, value, 'post');
    }
  } catch (err) {
    error(err);
  }
};
/**
 * @deleteAdminUsers
 *
 * Invoked when the Bulk Delete Admin Users Dialog is to be displayed or removed.
 */
export const deleteAdminUsers = (users: Users, show = true) => ({
  type : DELETE_ADMIN_USERS, users, show
});

/**
 * @deleteUsers
 *
 * Invoked when a collection of existing users are to be deleted, delegating to
 * the destroyUsers CTC API and dispatching a successful response or error action
 * accordingly.
 */
export const deleteUsers = (users: Users) => {
  return async(dispatch: Function): Promise<any> => {
    dispatch({type: DELETE_ADMIN_USERS_REQUEST});
    try {
      const {results, errors} = await destroyUsers(users);
      dispatch({type: DELETE_ADMIN_USERS_RESPONSE, users: results});
    } catch (err) {
      dispatch({type: DELETE_ADMIN_USERS_ERROR, error: err});
      error(err);
    }
  };
};

// Invoked by IonGrid when the grid filter model changes.
export const setFilterModel = (model: FilterModel) => ({
  type : SET_ADMIN_USERS_GRID_FILTER_MODEL, model
});
